import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
// Plain module-level flag — importing the editor here would pull Konva into the
// eager bundle and re-open the react/konva chunk cycle vite.config warns about.
import { isEditorDirty } from './lib/editorDirty';

// Unregister any existing service workers + caches from the previous PWA build.
// (The old PWA registered a root-scoped SW that serves a stale app shell; this
// cleans it up for any client that manages to load this fresh bundle.)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => registration.unregister());
  });
}
if (typeof caches !== 'undefined') {
  caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
}

// Handle chunk loading errors
window.addEventListener('error', (e) => {
  if (e.message?.includes('Failed to fetch dynamically imported module')) {
    const count = +(sessionStorage.getItem('chunk-reload') || 0);
    if (count < 2) {
      sessionStorage.setItem('chunk-reload', String(count + 1));
      console.log('🔄 Chunk error detected, reloading...');
      location.reload();
    }
  }
});

// ── Per-deploy cache busting ────────────────────────────────────────────────
// `version.json` is regenerated on every build (see vite.config.ts). Combined
// with `no-store` HTML + hashed/immutable assets + the SW kill-switch, a fresh
// navigation always loads the latest code. This polling layer additionally
// forces LONG-OPEN tabs to pick up new deploys, reloading only when the tab is
// backgrounded so we never interrupt someone mid-edit.
const fetchVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.version ?? null;
  } catch {
    return null;
  }
};

let reloadScheduled = false;
const reloadWhenIdle = () => {
  if (reloadScheduled) return;
  reloadScheduled = true;
  const go = () => {
    // A backgrounded tab cannot show a beforeunload prompt, so reloading here
    // would silently destroy unsaved editor work. Wait for it to be saved (the
    // editor autosaves on a 2s idle) and re-check rather than reloading blind.
    if (isEditorDirty()) {
      window.setTimeout(go, 30 * 1000);
      return;
    }
    sessionStorage.removeItem('chunk-reload');
    window.location.reload();
  };
  // Reload immediately if the tab is already hidden, otherwise wait until the
  // user switches away so active work isn't disrupted.
  if (document.hidden) go();
  else document.addEventListener('visibilitychange', () => { if (document.hidden) go(); }, { once: true });
};

const startVersionPolling = (current: string) => {
  setInterval(async () => {
    const latest = await fetchVersion();
    if (latest && latest !== current) {
      console.log('🔄 New deployment detected:', latest, '(running:', current + ')');
      localStorage.setItem('app_version', latest);
      reloadWhenIdle();
    }
  }, 5 * 60 * 1000); // every 5 minutes
};

// Render immediately (don't block first paint on the network), then record the
// running build version and start watching for new deployments.
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

fetchVersion().then((version) => {
  if (version) {
    localStorage.setItem('app_version', version);
    startVersionPolling(version);
  }
});